import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, X, History, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Btn, Badge, eur, dt } from "../components/common";
import { Modal, Field, Selecter, TextArea } from "./crm/crmui";
import { useAutoRefresh } from "../lib/autoRefresh";

const CAMPOS = [
  { value: "valor", label: "Valor" },
  { value: "total", label: "Total" },
  { value: "desconto_pct", label: "Desconto (%)" },
  { value: "margem_pct", label: "Margem (%)" },
  { value: "mrr", label: "MRR" },
  { value: "divida_aberta", label: "Divida em aberto do cliente" },
];
const OPS = [
  { value: "gt", label: "maior que" }, { value: "gte", label: "maior ou igual" },
  { value: "lt", label: "menor que" }, { value: "lte", label: "menor ou igual" },
  { value: "eq", label: "igual a" }, { value: "ne", label: "diferente de" },
];
const OBJETOS = ["venda", "desconto", "proposta", "contrato", "encomenda", "nota_credito", "ausencia"];
const ROLES = ["super_admin", "diretor_comercial", "financeiro", "comercial", "tecnico"];

const emptyPolicy = {
  nome: "", objeto: "venda", combinador: "OU",
  condicoes: [{ campo: "valor", op: "gt", valor: 0 }],
  aprovadores: "", modo: "paralelo", quorum: 0, auto_aprovar_valor: 0, isentar_roles: [],
};

export default function Approvals() {
  const [policies, setPolicies] = useState([]);
  const [requests, setRequests] = useState([]);
  const [showPolicy, setShowPolicy] = useState(false);
  const [showReq, setShowReq] = useState(false);
  const [policy, setPolicy] = useState(emptyPolicy);
  const [req, setReq] = useState({ objeto: "desconto", titulo: "", valor: 0, aprovadores: "" });
  const [decideModal, setDecideModal] = useState({ open: false, req: null, decisao: "aprovado", comentario: "" });
  const [historyReq, setHistoryReq] = useState(null);

  const load = async () => {
    try {
      const [p, r] = await Promise.all([api.get("/team/approval-policies"), api.get("/team/approvals")]);
      setPolicies(p.data); setRequests(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load); // eslint-disable-line

  const setCond = (i, key, val) => {
    const cs = policy.condicoes.map((c, idx) => idx === i ? { ...c, [key]: val } : c);
    setPolicy({ ...policy, condicoes: cs });
  };
  const addCond = () => setPolicy({ ...policy, condicoes: [...policy.condicoes, { campo: "valor", op: "gt", valor: 0 }] });
  const rmCond = (i) => setPolicy({ ...policy, condicoes: policy.condicoes.filter((_, idx) => idx !== i) });
  const toggleRole = (role) => {
    const has = policy.isentar_roles.includes(role);
    setPolicy({ ...policy, isentar_roles: has ? policy.isentar_roles.filter((r) => r !== role) : [...policy.isentar_roles, role] });
  };

  const createPolicy = async () => {
    if (!policy.nome.trim()) return toast.error("Indique o nome.");
    const aprovadores = policy.aprovadores.split(",").map((s) => s.trim()).filter(Boolean);
    if (aprovadores.length === 0) return toast.error("Indique pelo menos um aprovador.");
    const seen = new Set();
    const condicoes = [];
    for (const c of policy.condicoes) {
      const key = `${c.campo}|${c.op}|${parseFloat(c.valor) || 0}`;
      if (seen.has(key)) continue;
      seen.add(key);
      condicoes.push({ campo: c.campo, op: c.op, valor: parseFloat(c.valor) || 0 });
    }
    const payload = {
      nome: policy.nome, objeto: policy.objeto, combinador: policy.combinador,
      condicoes, aprovadores,
      modo: policy.modo, quorum: parseInt(policy.quorum) || 0,
      auto_aprovar_valor: parseFloat(policy.auto_aprovar_valor) || 0,
      isentar_roles: policy.isentar_roles,
    };
    try { await api.post("/team/approval-policies", payload); toast.success("Politica criada."); setShowPolicy(false); setPolicy(emptyPolicy); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deletePolicy = async (id) => {
    try { await api.delete(`/team/approval-policies/${id}`); toast.success("Politica removida."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createReq = async () => {
    if (!req.titulo.trim()) return toast.error("Indique o titulo.");
    const payload = { ...req, valor: parseFloat(req.valor) || 0, aprovadores: req.aprovadores.split(",").map((s) => s.trim()).filter(Boolean) };
    try { await api.post("/team/approvals", payload); toast.success("Pedido criado."); setShowReq(false); setReq({ objeto: "desconto", titulo: "", valor: 0, aprovadores: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const confirmDecide = async () => {
    const { req: r, decisao, comentario } = decideModal;
    try {
      await api.post(`/team/approvals/${r.id}/decide`, { decisao, comentario });
      toast.success(`Pedido ${decisao}.`);
      setDecideModal({ open: false, req: null, decisao: "aprovado", comentario: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const condSummary = (r) => {
    const cs = r.condicoes && r.condicoes.length ? r.condicoes : [{ campo: r.condicao_campo, op: r.condicao_op, valor: r.condicao_valor }];
    const map = Object.fromEntries(OPS.map((o) => [o.value, o.label]));
    return cs.map((c) => `${c.campo} ${map[c.op] || c.op} ${c.valor}`).join(` ${r.combinador || "OU"} `);
  };

  const polCols = [
    { key: "nome", label: "Nome", render: (r) => <span className="font-semibold text-navy">{r.nome}</span> },
    { key: "objeto", label: "Objeto", render: (r) => <Badge value={r.objeto} /> },
    { key: "regra", label: "Condicao", render: (r) => <span className="text-xs text-muted2">{condSummary(r)}</span> },
    { key: "aprov", label: "Aprovadores", render: (r) => <span className="text-xs text-muted2">{(r.aprovadores || []).join(", ") || "-"}<br />{r.modo || "paralelo"}{r.quorum ? ` (quorum ${r.quorum})` : ""}</span> },
    { key: "excecoes", label: "Excecoes", render: (r) => <span className="text-xs text-muted2">{r.auto_aprovar_valor ? `auto <= ${r.auto_aprovar_valor}` : ""}{r.isentar_roles && r.isentar_roles.length ? ` isenta: ${r.isentar_roles.join(",")}` : ""}{!r.auto_aprovar_valor && !(r.isentar_roles || []).length ? "-" : ""}</span> },
    { key: "acoes", label: "", render: (r) => <button onClick={() => deletePolicy(r.id)} data-testid={`del-policy-${r.id}`} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={16} /></button> },
  ];

  const reqCols = [
    { key: "titulo", label: "Titulo", render: (r) => <span className="font-semibold text-navy">{r.titulo}</span> },
    { key: "objeto", label: "Objeto" },
    { key: "valor", label: "Valor", render: (r) => eur(r.valor) },
    { key: "estado", label: "Estado", render: (r) => <Badge value={r.estado} /> },
    { key: "progresso", label: "Progresso", render: (r) => (r.aprovacoes_necessarias > 1 || r.modo === "sequencial")
      ? <span className="text-xs text-muted2">{r.aprovacoes_dadas || (r.decisoes || []).filter((d) => d.decisao === "aprovado").length}/{r.aprovacoes_necessarias || 1} <span className="text-slate-400">({r.modo || "paralelo"})</span></span>
      : "-" },
    { key: "decidido", label: "Decisao", render: (r) => r.decidido_por ? <span className="text-xs text-muted2">{r.decidido_por} <span className="text-slate-400">(via {r.decidido_via || "plataforma"})</span></span> : "-" },
    { key: "criado_em", label: "Criado", render: (r) => dt(r.criado_em) },
    { key: "acoes", label: "", render: (r) => (
      <div className="flex gap-1 items-center">
        {r.estado === "pendente" && (
          <>
            <button onClick={() => setDecideModal({ open: true, req: r, decisao: "aprovado", comentario: "" })} data-testid={`approve-${r.id}`} className="text-green-600 hover:bg-green-50 rounded p-1"><Check size={16} /></button>
            <button onClick={() => setDecideModal({ open: true, req: r, decisao: "rejeitado", comentario: "" })} data-testid={`reject-${r.id}`} className="text-red-600 hover:bg-red-50 rounded p-1"><X size={16} /></button>
          </>
        )}
        {(r.decisoes || []).length > 0 && (
          <button onClick={() => setHistoryReq(r)} data-testid={`history-${r.id}`} className="text-navy hover:bg-tint rounded p-1"><History size={16} /></button>
        )}
      </div>
    ) },
  ];

  return (
    <>
      <PageHeader overline="Equipa" title="Aprovacoes" action={<div className="flex gap-2"><Btn variant="ghost" onClick={() => setShowPolicy(true)} testid="new-policy-btn"><Plus size={16} /> Nova politica</Btn><Btn onClick={() => setShowReq(true)} testid="new-approval-btn"><Plus size={16} /> Novo pedido</Btn></div>} />

      <h2 className="text-lg font-bold text-navy mb-3">Pedidos de aprovacao</h2>
      <Table testid="approvals-table" columns={reqCols} rows={requests} />

      <h2 className="text-lg font-bold text-navy mt-8 mb-3">Politicas</h2>
      <p className="text-sm text-muted2 mb-3">As politicas determinam quando uma proposta, contrato ou venda exige aprovacao. Suportam varias condicoes (incluindo divida em aberto do cliente), multi-aprovador e excecoes.</p>
      <Table testid="policies-table" columns={polCols} rows={policies} />

      <Modal open={showPolicy} onClose={() => setShowPolicy(false)} title="Nova politica de aprovacao" testid="policy-modal">
        <Field label="Nome" value={policy.nome} onChange={(v) => setPolicy({ ...policy, nome: v })} testid="policy-nome" />
        <div className="grid grid-cols-2 gap-3">
          <Selecter label="Objeto" value={policy.objeto} onChange={(v) => setPolicy({ ...policy, objeto: v })} options={OBJETOS} testid="policy-objeto" />
          <Selecter label="Combinar condicoes" value={policy.combinador} onChange={(v) => setPolicy({ ...policy, combinador: v })} options={[{ value: "OU", label: "OU (qualquer)" }, { value: "E", label: "E (todas)" }]} testid="policy-combinador" />
        </div>

        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Condicoes</label>
          <div className="space-y-2">
            {policy.condicoes.map((c, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`policy-cond-${i}`}>
                <select value={c.campo} onChange={(e) => setCond(i, "campo", e.target.value)} data-testid={`policy-cond-campo-${i}`} className="px-2 py-2 border border-tint rounded-lg text-sm bg-white">
                  {CAMPOS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select value={c.op} onChange={(e) => setCond(i, "op", e.target.value)} data-testid={`policy-cond-op-${i}`} className="px-2 py-2 border border-tint rounded-lg text-sm bg-white">
                  {OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <input type="number" value={c.valor} onChange={(e) => setCond(i, "valor", e.target.value)} data-testid={`policy-cond-valor-${i}`} className="px-2 py-2 border border-tint rounded-lg text-sm" />
                <button onClick={() => rmCond(i)} disabled={policy.condicoes.length === 1} className="text-red-600 disabled:text-slate-300 p-1"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
          <button onClick={addCond} data-testid="policy-add-cond" className="mt-2 text-sm text-blue font-semibold">+ Adicionar condicao</button>
        </div>

        <Field label="Aprovadores (emails separados por virgula)" value={policy.aprovadores} onChange={(v) => setPolicy({ ...policy, aprovadores: v })} placeholder="comercial@cogniseg.com, financeiro@cogniseg.com" testid="policy-aprovadores" />
        <div className="grid grid-cols-2 gap-3">
          <Selecter label="Modo" value={policy.modo} onChange={(v) => setPolicy({ ...policy, modo: v })} options={[{ value: "paralelo", label: "Paralelo (quorum)" }, { value: "sequencial", label: "Sequencial (por ordem)" }]} testid="policy-modo" />
          <Field label="Quorum (0 = todos)" type="number" value={policy.quorum} onChange={(v) => setPolicy({ ...policy, quorum: v })} testid="policy-quorum" />
        </div>

        <div className="border-t border-tint pt-3">
          <p className="text-sm font-semibold text-navy mb-2">Excecoes</p>
          <Field label="Auto-aprovar quando valor <= (0 = desligado)" type="number" value={policy.auto_aprovar_valor} onChange={(v) => setPolicy({ ...policy, auto_aprovar_valor: v })} testid="policy-auto" />
          <label className="block text-sm font-semibold text-navy mt-3 mb-1">Isentar papeis</label>
          <div className="flex flex-wrap gap-2">
            {ROLES.map((role) => (
              <button key={role} onClick={() => toggleRole(role)} data-testid={`policy-isentar-${role}`}
                className={`text-xs px-3 py-1 rounded-full border ${policy.isentar_roles.includes(role) ? "bg-navy text-white border-navy" : "bg-white text-muted2 border-tint"}`}>{role}</button>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShowPolicy(false)}>Cancelar</Btn><Btn onClick={createPolicy} testid="policy-save">Criar</Btn></div>
      </Modal>

      <Modal open={showReq} onClose={() => setShowReq(false)} title="Novo pedido de aprovacao" testid="approval-modal">
        <Field label="Titulo" value={req.titulo} onChange={(v) => setReq({ ...req, titulo: v })} testid="approval-titulo" />
        <Selecter label="Objeto" value={req.objeto} onChange={(v) => setReq({ ...req, objeto: v })} options={[...OBJETOS]} testid="approval-objeto" />
        <Field label="Valor" type="number" value={req.valor} onChange={(v) => setReq({ ...req, valor: v })} testid="approval-valor" />
        <Field label="Aprovadores (emails separados por virgula, opcional)" value={req.aprovadores} onChange={(v) => setReq({ ...req, aprovadores: v })} placeholder="comercial@cogniseg.com, financeiro@cogniseg.com" testid="approval-aprovadores" />
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShowReq(false)}>Cancelar</Btn><Btn onClick={createReq} testid="approval-save">Criar</Btn></div>
      </Modal>

      <Modal open={decideModal.open} onClose={() => setDecideModal({ ...decideModal, open: false })} title={`${decideModal.decisao === "aprovado" ? "Aprovar" : "Rejeitar"} pedido`} testid="decide-modal">
        <p className="text-sm text-muted2">{decideModal.req?.titulo}</p>
        <TextArea label="Comentario (visivel para o solicitante)" value={decideModal.comentario} onChange={(v) => setDecideModal({ ...decideModal, comentario: v })} testid="decide-comentario" />
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="ghost" onClick={() => setDecideModal({ ...decideModal, open: false })}>Cancelar</Btn>
          <Btn onClick={confirmDecide} testid="decide-confirm">{decideModal.decisao === "aprovado" ? "Aprovar" : "Rejeitar"}</Btn>
        </div>
      </Modal>

      <Modal open={!!historyReq} onClose={() => setHistoryReq(null)} title="Historico de decisoes" testid="history-modal">
        <p className="text-sm font-semibold text-navy mb-2">{historyReq?.titulo}</p>
        {historyReq?.motivos && historyReq.motivos.length > 0 && (
          <p className="text-xs text-muted2 mb-2">Motivo: {historyReq.motivos.join("; ")}</p>
        )}
        <div className="space-y-2" data-testid="history-list">
          {(historyReq?.decisoes || []).length === 0 && <p className="text-sm text-muted2">Sem decisoes.</p>}
          {(historyReq?.decisoes || []).map((d, i) => (
            <div key={i} className="text-sm bg-tint rounded-lg p-2">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-navy">{d.email || d.por}</span>
                <Badge value={d.decisao} />
              </div>
              <div className="text-xs text-muted2">{dt(d.em)} - via {d.via || "plataforma"}</div>
              {d.comentario && <div className="mt-1 text-navy">{d.comentario}</div>}
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
}
